import { useId, useCallback, useEffect } from 'react';
import { Popup } from 'maplibre-gl';
import { map } from './core/MapView';
import { findFonts } from './core/mapUtil';
import { mapPOIIconKey } from './core/preloadImages';
import { useAttributePreference } from '../common/util/preferences';
import { useEffectAsync } from '../reactHelper';
import usePersistedState from '../common/util/usePersistedState';

const MapPOILocations = () => {
  const id = useId();
  const clusters = `${id}-clusters`;
  const direction = `${id}-direction`;

  const iconScale = useAttributePreference('iconScale', 1);
  const mapCluster = useAttributePreference('mapCluster', true);

  const timestamp = Date.now();

  const positions = usePersistedState('poilocations')[0];

  useEffectAsync(async () => {
    console.log(`RECEIVED ${positions.length} MARKERS`);
  }, [timestamp]);

  const createFeature = (poiLocation) => ({
    id: poiLocation.id,
    name: poiLocation.name,
    category: mapPOIIconKey(poiLocation.icon),
    color: poiLocation.color,
    html: poiLocation.html,
  });

  const onMouseEnter = () => map.getCanvas().style.cursor = 'pointer';
  const onMouseLeave = () => map.getCanvas().style.cursor = '';

  const onClick = (feature) => {
    new Popup()
      .setLngLat(feature.geometry.coordinates.slice())
      .setHTML(feature.properties.html)
      .addTo(map);
  };

  const onMarkerClick = useCallback((event) => {
    event.preventDefault();
    const feature = event.features[0];
    onClick(feature);
  });

  const onClusterClick = useCallback((event) => {
    event.preventDefault();
    const features = map.queryRenderedFeatures(event.point, {
      layers: [clusters],
    });
    const clusterId = features[0].properties.cluster_id;
    map.getSource(id).getClusterExpansionZoom(clusterId, (error, zoom) => {
      if (!error) {
        map.easeTo({
          center: features[0].geometry.coordinates,
          zoom,
        });
      }
    });
  }, [clusters]);

  useEffect(() => {
    map.addSource(id, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
      cluster: mapCluster,
      clusterMaxZoom: 14,
      clusterRadius: 50,
    });
    map.addLayer({
      id,
      type: 'symbol',
      source: id,
      filter: ['!has', 'point_count'],
      layout: {
        'icon-image': '{category}-{color}',
        'icon-size': iconScale / 1.5,
        'icon-allow-overlap': true,
        'text-field': '{name}',
        'text-allow-overlap': true,
        'text-anchor': 'bottom',
        'text-offset': [0, -1 * iconScale],
        'text-font': findFonts(map),
        'text-size': 15,
      },
      paint: {
        'text-halo-color': '#1c2138',
        'text-halo-width': 4,
        'text-color': 'white',
      },
    });
    map.addLayer({
      id: clusters,
      type: 'symbol',
      source: id,
      filter: ['has', 'point_count'],
      layout: {
        'icon-image': 'poiBackground',
        'icon-size': iconScale / 1.5,
        'text-field': '{point_count_abbreviated}',
        'text-font': findFonts(map),
        'text-size': 14,
      },
      paint: {
        'text-color': 'white',
      },
    });

    map.on('mouseenter', id, onMouseEnter);
    map.on('mouseleave', id, onMouseLeave);
    map.on('mouseenter', clusters, onMouseEnter);
    map.on('mouseleave', clusters, onMouseLeave);
    map.on('click', id, onMarkerClick);
    map.on('click', clusters, onClusterClick);

    return () => {
      map.off('mouseenter', id, onMouseEnter);
      map.off('mouseleave', id, onMouseLeave);
      map.off('mouseenter', clusters, onMouseEnter);
      map.off('mouseleave', clusters, onMouseLeave);
      map.off('click', id, onMarkerClick);
      map.off('click', clusters, onClusterClick);

      if (map.getLayer(id)) {
        map.removeLayer(id);
      }
      if (map.getLayer(clusters)) {
        map.removeLayer(clusters);
      }
      if (map.getLayer(direction)) {
        map.removeLayer(direction);
      }
      if (map.getSource(id)) {
        map.removeSource(id);
      }
    };
  }, [mapCluster, clusters, direction, onMarkerClick, onClusterClick]);

  useEffect(() => {
    if (positions) {
      map.getSource(id).setData({
        type: 'FeatureCollection',
        features: positions.map((poilocation) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [poilocation.longitude, poilocation.latitude],
          },
          properties: createFeature(poilocation),
        })),
      });
    } else {
      console.log('ERRORE');
    }
  });
  return null;
};

export default MapPOILocations;
